import {getDeviceNameSync, getUniqueIdSync} from 'react-native-device-info';

import {Config} from '@buncha/utils/types';
import {NetworkConfiguration} from '../graphQL/type';
import {Operation} from '@apollo/client';
import {Platform} from 'react-native';
import {getConfig} from '@buncha/utils/appInfo';

export function getEntityName() {
  if (Platform.OS === 'ios')
    switch (process.env.DEPLOY_TYPE) {
      case NetworkConfiguration.developement1:
        return 'Dev-Shopper_iOS';
      case NetworkConfiguration.preproduction:
        return 'Prod-Shopper_iOS';
      case NetworkConfiguration.production:
        return 'Prod-Shopper_iOS';
      default:
        return 'Dev-Shopper_iOS';
    }

  switch (process.env.DEPLOY_TYPE) {
    case NetworkConfiguration.developement1:
      return 'Dev-Shopper_Android';
    case NetworkConfiguration.preproduction:
      return 'Prod-Shopper_Android';
    case NetworkConfiguration.production:
      return 'Prod-Shopper_Android';
    default:
      return 'Dev-Shopper_Android';
  }
}

export const constantFields = {
  entity: {
    name: getEntityName(),
  },
  deviceType: Platform.OS,
  source: 'fulfillment',
};

export interface LogApiFormatterInput {
  info?: any;
  response: any;
  sessionId: string;
  error: any;
  url: string;
  userId?: number;
  loginToken: string;
  operation?: Operation;
  timeTaken?: number;
  traceId: string;
  retryCount?: number;
  delay?: boolean;
  delayLatency?: number;
  startTime?: number;
  backgroundTime?: number;
  lastTimeStamp?: number;
}

export const metaInfoConstants = {
  appVersion: getConfig(Config.versionName),
  deviceId: getUniqueIdSync(),
  mobileDevice: getDeviceNameSync(),
  osVersion: Platform.Version,
  deviceType: Platform.OS,
};

export function hideValue(theObject: any, fields: string[]): any {
  if (!theObject) return null;
  if (Array.isArray(theObject))
    return theObject.map((obj: any) => hideValue(obj, fields));

  if (['number', 'string', 'boolean'].includes(typeof theObject))
    return theObject;

  const modifiedObject: any = {};
  Object.entries(theObject).forEach(([key, value]) => {
    if (fields.includes(key)) modifiedObject[key] = 'Hidden from Buncha';
    else modifiedObject[key] = hideValue(value, fields);
  });
  return modifiedObject;
}

export const formatToLogForApis = (data: LogApiFormatterInput) => {
  const info = data?.info;
  const operation = data?.operation;
  const response = data?.response;
  const error = data?.error;
  const sessionId = data?.sessionId;
  const operationName =
    info?.query?.definitions?.[0]?.name?.value ||
    info?.mutation?.definitions?.[0]?.name?.value ||
    operation?.operationName;
  let variables;
  const variablesCopy = JSON.parse(
    JSON.stringify(info?.variables ?? operation?.variables ?? {}),
  );
  try {
    variables = hideValue(variablesCopy, ['base64']);
  } catch (err: any) {
    variables = info?.variables || operation?.variables;
  }
  const type = !response && !error ? 'request' : 'response';

  const formattedData = {
    ...constantFields,
    level: 'meta',
    metaInfo: {
      ...metaInfoConstants,
      operationName,
      timestamp: data.startTime,
      hasError: Boolean(error),
      type: type,
      userId: data.userId,
      latency: data?.timeTaken,
      retryCount: data?.retryCount,
      delay: data?.delay,
      delayLatency: data?.delayLatency,
      backgroundTime: data?.backgroundTime,
      lastTimeStamp: data?.lastTimeStamp,
    },
    url: data.url,
    reqBody: {
      variables,
      query:
        info?.query?.loc?.source?.body?.trim() ??
        operation?.query?.loc?.source?.body?.trim() ??
        operation?.query,
    },
    reqHeaders: {
      authorization: `Bearer ${data.loginToken}`,
      sessionId,
      traceid: data.traceId,
      startTime: data.startTime,
    },
    resBody: response,
    error,
    message: `${type}:shopper:${operationName}`,
    timestamp: Date.now(),
    logVersion: 6,
  };

  return formattedData;
};

// eslint-disable-next-line no-shadow
export enum LogLevel {
  info = 'info',
  error = 'error',
}

export const MAX_LOG_FILE_SIZE = 900000;
